/**
 * ProviderMark — the single decision point for rendering a vendor's brand
 * mark in the app's light/dark themes.
 *
 * A brand mark is NOT theme-neutral. Multi-colour marks (Jira, Slack, the
 * Google marks) read on both backgrounds, but monochrome marks (Linear)
 * ship in a dark-glyph and a light-glyph variant, and rendering the wrong
 * one makes the mark invisible (dark-on-dark — the 2026-08-15 Connected
 * Sources defect). The theme choice is made HERE, once, via the same
 * `dark:` class mechanism the rest of the app uses (`:root.dark`,
 * `darkMode: 'class'`) — never per-component, and never with CSS filters
 * or recolouring, which vendor brand guidelines prohibit.
 *
 * Assets are the official vendor files under `frontend/public` (SVG where
 * the vendor offers it; Google Workspace product icons are vendor PNGs at
 * native 1x/2x — Google does not publish them as SVG). Any new vendor
 * logo added to the codebase gets registered here with both variants if
 * it is monochrome.
 */

interface MarkAsset {
  /** Asset for light backgrounds (and the only asset for full-colour marks). */
  light: string;
  /** Light-glyph variant for dark backgrounds. Omit for full-colour marks. */
  dark?: string;
  srcSet?: string;
  label: string;
}

const MARKS: Record<string, MarkAsset> = {
  jira: { light: '/integrations/jira.svg', label: 'Jira' },
  linear: {
    light: '/integrations/linear-dark.svg',
    dark: '/integrations/linear-light.svg',
    label: 'Linear',
  },
  slack: { light: '/integrations/slack.svg', label: 'Slack' },
  google: { light: '/integrations/google.svg', label: 'Google' },
  google_drive: {
    light: '/integrations/google/drive-32.png',
    srcSet: '/integrations/google/drive-32.png 1x, /integrations/google/drive-64.png 2x',
    label: 'Google Drive',
  },
};

export function ProviderMark({
  provider,
  className = 'h-4 w-4',
  alt,
}: {
  provider: string;
  className?: string;
  alt?: string;
}) {
  const mark = MARKS[provider];
  if (!mark) return null;
  if (!mark.dark) {
    return (
      <img
        src={mark.light}
        srcSet={mark.srcSet}
        alt={alt ?? mark.label}
        className={`${className} object-contain`}
      />
    );
  }
  return (
    <>
      <img
        src={mark.light}
        alt={alt ?? mark.label}
        className={`${className} object-contain dark:hidden`}
      />
      <img
        src={mark.dark}
        alt={alt ?? mark.label}
        className={`${className} object-contain hidden dark:block`}
      />
    </>
  );
}
